import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { UserInfo, UserRole } from '../types/user';
import { redirectToLogin } from '../lib/okta/oktaConfig';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Mappa per convertire stringhe del backend in valori enum
const roleStringToEnum: Record<string, UserRole> = {
  'Admin': UserRole.Admin,
  'CapoUfficioStile': UserRole.AdminUfficioStile,
  'UfficioStile': UserRole.OperatoreUfficioStile,
  'CapoIndustrializzazione': UserRole.AdminSviluppo,
  'Industrializzazione': UserRole.OperatoreSviluppo,
};

export const useCurrentUser = (accessToken: string | null) => {
  return useQuery<UserInfo>({
    queryKey: ['currentUser', accessToken],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error('No access token available');
      }

      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/User/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (err) {
        // 401 = token non più valido: login. 403 (nessun ruolo) NON reindirizza, per evitare loop.
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          redirectToLogin();
        }
        throw err;
      }

      // Il backend potrebbe restituire i dati wrappati in {data: ...} o direttamente
      const userData = response.data.data || response.data;
      
      // Converti le stringhe dei ruoli in valori enum numerici
      if (userData && Array.isArray(userData.roles)) {
        userData.roles = userData.roles
          .map((role: string | UserRole) => {
            // Se è già un numero (enum), restituiscilo così com'è
            if (typeof role === 'number') return role;
            // Se è una stringa, convertila usando la mappa
            return roleStringToEnum[role];
          })
          .filter((role: UserRole | undefined) => role !== undefined); // Rimuovi ruoli non mappati
      } else {
        userData.roles = [];
      }
      
      
      return userData;
    },
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minuti
    retry: 1,
  });
};
